import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { MdOutlinePolicy } from 'react-icons/md'
import useFetch, { CachePolicies } from 'use-http'
import { colors, IKReactSelect } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import {
  GetPathPolicyList,
  GetPathPolicyResponse,
  PathPolicyErrorResponse,
} from '../../interfaces'
import { MLContext, UserContext } from '../../providers'

interface PolicyOption {
  value: string
  label: string
}

interface Props {
  isOpen: boolean
  onClose(): void
}

export const ApplyPathPolicyModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const [{ selectedFolders }] = useContext(MLContext)

  const [formError, setFormError] = useState('')
  const [options, setOptions] = useState<PolicyOption[]>([])
  const [selectedOption, setSelectedOption] = useState<PolicyOption | null>(
    null,
  )
  const [initialOption, setInitialOption] = useState<PolicyOption | null>(null)
  const [inheritedPolicy, setInheritedPolicy] =
    useState<GetPathPolicyList | null>(null)

  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getAndSetPathPolicies = async () => {
    const res = (await request.get(
      `/api/clients/${user?.clientNumber}/path-policy`,
    )) as GetPathPolicyList[]

    if (request.error) {
      setFormError(
        t('error_in_fetching_path_policies', 'Error in fetching path policies'),
      )
      setOptions([])
      return
    }

    setOptions(
      res.map(policy => ({
        value: policy.pathPolicyId,
        label: policy.name,
      })),
    )

    const currentPolicyRes = (await request.get(
      `/api/clients/${user?.clientNumber}/path-policy?folderPath=${selectedFolders[0].folderPath}`,
    )) as GetPathPolicyList[]

    if (request.error) {
      setFormError(
        t(
          'error_in_fetching_path_policy_for_folder',
          'Error in fetching path policies for folder',
        ),
      )
      setOptions([])
      return
    }

    if (currentPolicyRes.length === 0) {
      return
    }

    if (currentPolicyRes[0].inheritedFrom) {
      setInheritedPolicy(currentPolicyRes[0])
    }

    setInitialOption({
      label: currentPolicyRes[0].name,
      value: currentPolicyRes[0].pathPolicyId,
    })

    setSelectedOption({
      label: currentPolicyRes[0].name,
      value: currentPolicyRes[0].pathPolicyId,
    })
  }

  useEffect(() => {
    void getAndSetPathPolicies()
  }, [])

  const handlePolicyChange = async () => {
    setFormError('')

    let showRemoveToast = false
    if (initialOption && initialOption.value !== selectedOption?.value) {
      if (initialOption.label === inheritedPolicy?.name && !selectedOption) {
        setFormError(
          t(
            'policy_inherited_from_parent_folder_error',
            'This path policy comes from a parent folder. Delete it in that parent folder to remove it here.',
          ),
        )
        return
      }
      const rem = (await request.put(
        `/api/clients/${user?.clientNumber}/path-policy/${initialOption.value}`,
        { removeFolderPath: selectedFolders[0].folderPath },
      )) as GetPathPolicyResponse | PathPolicyErrorResponse

      if (request.error) {
        setFormError(
          (rem as PathPolicyErrorResponse).message ||
            t(
              'error_removing_path_policy_from_folder',
              'Error removing path from old policy',
            ),
        )
        return
      }
      showRemoveToast = true
    }

    if (!selectedOption && showRemoveToast) {
      toast({
        description: t(
          'path_policy_removed_successfully',
          'Path policy removed successfully',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      onClose()
      return
    }

    if (!selectedOption) {
      return
    }

    const res = (await request.put(
      `/api/clients/${user?.clientNumber}/path-policy/${selectedOption.value}`,
      { addFolderPath: selectedFolders[0].folderPath },
    )) as GetPathPolicyResponse | PathPolicyErrorResponse

    if (request.error) {
      setFormError(
        (res as PathPolicyErrorResponse).message ??
          t(
            'error_in_applying_path_policy_to_folder',
            'Error in applying path policy to folder',
          ),
      )
    } else {
      toast({
        description: t(
          'path_policy_applied_to_folder_path',
          'Path policy applied to folder path',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={MdOutlinePolicy}
            color="appBattleshipGrey"
            backgroundColor="appPaleGrey"
            boxSize={6}
            padding="0.1em"
            borderRadius="full"
            marginRight="0.5em"
          />
          {t('apply_path_policy_to_folder', 'Apply path policy to folder')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {formError ? (
            <Alert status="error" mb="1em" minW="unset">
              <AlertIcon />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}
          {inheritedPolicy ? (
            <Alert status="info" mb="1em" minW="unset">
              <AlertIcon />
              <AlertDescription>
                <Trans
                  i18nKey="path_policy_inherited_from"
                  values={{ path: inheritedPolicy.inheritedFrom }}
                >
                  This path policy is inherited from{' '}
                  "{{ path: inheritedPolicy.inheritedFrom }}"
                </Trans>
              </AlertDescription>
            </Alert>
          ) : null}
          <IKReactSelect
            variant="compact"
            options={options}
            isClearable
            placeholder={t('select_a_policy', 'Select a policy')}
            value={selectedOption}
            onChange={opt => {
              setSelectedOption(opt)
            }}
            isDisabled={loading}
            isLoading={loading}
          />
        </ModalBody>

        <ModalFooter
          borderTop={`1px solid ${colors.appGrey}`}
          style={{ gap: '1em' }}
        >
          <Button
            onClick={() => {
              onClose()
              setFormError('')
            }}
            variant="outline"
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            isDisabled={
              loading || initialOption?.value === selectedOption?.value
            }
            isLoading={loading}
            onClick={async () => {
              await handlePolicyChange()
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ApplyPathPolicyModal
